import { CARCASS_MM, HALF_CARCASS_MM } from "./carcass";
import type { InternalItem, WardrobeModule } from "../types";
import { cabinetBaseHeightMm } from "./installation";
import { SHOE_SHELF_LIP_MM } from "./wardrobeFittings";

export const GARMENT_SLOT_SPACING_MM = 52;
const HANGING_CLEARANCE_MM = 35;
const cabinetBottomClearanceMm = (module: WardrobeModule) =>
  module.type === "mezzanine"
    ? CARCASS_MM
    : Math.max(80, cabinetBaseHeightMm(module) + CARCASS_MM);

type ClothesType = NonNullable<InternalItem["clothes"]>;

const GARMENT_TYPE_SEQUENCE: Record<ClothesType, ClothesType[]> = {
  shirts: [
    "shirts",
    "shirts",
    "jackets",
    "shirts",
    "shortDress",
    "shirts",
    "jackets",
    "shirts"
  ],
  jackets: [
    "jackets",
    "shirts",
    "jackets",
    "shortDress",
    "jackets",
    "shirts"
  ],
  shortDress: [
    "shortDress",
    "shirts",
    "shortDress",
    "jackets",
    "shortDress",
    "shirts"
  ],
  longDress: [
    "longDress",
    "shirts",
    "longDress",
    "jackets",
    "shortDress",
    "longDress",
    "shirts"
  ],
  coats: [
    "coats",
    "jackets",
    "coats",
    "shirts",
    "coats",
    "shortDress"
  ]
};

export type HangingSlot = {
  x: number;
  segmentStart: number;
  segmentEnd: number;
};

export const clothesRequiredHeight = (
  clothes: InternalItem["clothes"] = "shirts"
) =>
  clothes === "coats"
    ? 1400
    : clothes === "longDress"
        ? 1300
      : clothes === "shortDress"
        ? 1050
        : clothes === "jackets"
          ? 800
          : 860;

const blockerTop = (item: InternalItem) => {
  if (item.type === "shelf") return item.y + HALF_CARCASS_MM;
  // Над обувницей — её бортик.
  if (item.type === "shoeRack") return item.y + HALF_CARCASS_MM + SHOE_SHELF_LIP_MM;
  // Ящики, корзины и брючница висят под своей отметкой.
  return item.y;
};

/** Нижняя штанга углового: вещи верхней заканчиваются над крючками плечиков. */
const LOWER_RAIL_HOOKS_MM = 25;

export const availableHangingHeight = (
  module: WardrobeModule,
  rail: InternalItem
) => {
  // В угловом все штанги вдоль одной стены (cornerInternals) — нижняя штанга
  // ограничивает вещи верхней, как полка.
  const top = (item: InternalItem) => item.type === "rail" ? item.y + LOWER_RAIL_HOOKS_MM : blockerTop(item);
  const blocker = module.items
    .filter(
      (candidate) =>
        candidate.id !== rail.id &&
        (["shelf", "drawer", "basket", "leatherBasket", "shoeRack", "trouserRack"].includes(candidate.type) ||
          (module.type === "corner45" && candidate.type === "rail")) &&
        top(candidate) < rail.y
    )
    .sort((a, b) => top(b) - top(a))[0];

  const lowerLimit = blocker
    ? top(blocker)
    : cabinetBottomClearanceMm(module);
  return Math.max(0, rail.y - lowerLimit - HANGING_CLEARANCE_MM);
};

export const garmentFitsBelowRail = (
  module: WardrobeModule,
  rail: InternalItem,
  clothes: InternalItem["clothes"] = "shirts"
) => {
  const requiredHeight = clothesRequiredHeight(clothes);
  return availableHangingHeight(module, rail) >= requiredHeight;
};

/**
 * Одежду на штанге не выбирают: она подбирается по свободной высоте под
 * штангой (решение Сергея 28.09). Подняли штангу — вещи длиннее, опустили —
 * короче. От самой длинной к самой короткой.
 */
const CLOTHES_BY_LENGTH: ClothesType[] = ["coats", "longDress", "shortDress", "shirts", "jackets"];

export const railClothes = (module: WardrobeModule, rail: InternalItem): ClothesType =>
  CLOTHES_BY_LENGTH.find((clothes) => garmentFitsBelowRail(module, rail, clothes)) ?? "shirts";

const CLOTHES_FIT_TEXT: Record<ClothesType, string> = {
  coats: "пальто и одежда в пол",
  longDress: "длинные платья и плащи",
  shortDress: "платья и куртки",
  shirts: "рубашки и пиджаки",
  jackets: "пиджаки и короткие вещи"
};

/** «Под штангой 1450 мм — пальто и одежда в пол». */
export const railClothesText = (module: WardrobeModule, rail: InternalItem) => {
  const height = Math.round(availableHangingHeight(module, rail));
  return clothesFitBelowRail(module, rail)
    ? `Под штангой ${height} мм — ${CLOTHES_FIT_TEXT[railClothes(module, rail)]}`
    : `Под штангой ${height} мм — одежде не хватает места. Поднимите штангу или опустите полку под ней`;
};

/** Помещается ли под штангой хоть что-то. */
export const clothesFitBelowRail = (
  module: WardrobeModule,
  rail: InternalItem
) => garmentFitsBelowRail(module, rail, CLOTHES_BY_LENGTH.at(-1));

export const fittingGarmentSequence = (
  module: WardrobeModule,
  rail: InternalItem
) =>
  GARMENT_TYPE_SEQUENCE[railClothes(module, rail)].filter((clothes) =>
    garmentFitsBelowRail(module, rail, clothes)
  );

export const hangingSlots = (
  segments: Array<[number, number]>,
  spacing = GARMENT_SLOT_SPACING_MM
): HangingSlot[] =>
  segments.flatMap(([segmentStart, segmentEnd]) => {
    const length = Math.max(0, segmentEnd - segmentStart);
    if (length <= 0) return [];

    const count = Math.max(
      1,
      Math.min(22, Math.floor((length + spacing * 0.4) / spacing))
    );
    const edgeInset = Math.min(55, length / 2);
    const step = count > 1 ? (length - edgeInset * 2) / (count - 1) : 0;

    return Array.from({ length: count }, (_, index) => ({
      x: segmentStart + edgeInset + index * step,
      segmentStart,
      segmentEnd
    }));
  });
